import { Select, Skeleton } from "antd"
import ShiftRateCard from "./ShiftRateCard"
import { useGet } from "utils/useFetch"
import { IAnalyticsRes } from "../typings"
import { useParams, useSearchParams } from "react-router-dom"
import { ShiftRateCardWrapper } from "../overview/component/ShiftRateCardWrapper"

type WorkPlaceAnalysisWrapperProps = {
  timeline: string
  title: string
  setSelectedTimeline: (value: string) => void
}

const WorkPlaceAnalysisWrapper = ({
  title,
  timeline,
  setSelectedTimeline,
}: WorkPlaceAnalysisWrapperProps) => {
  const { x } = useParams()
  const [searchParams] = useSearchParams()
  const dutyLocationIdRaw = searchParams.get("dutyLocationId")
  const dutyLocationId = dutyLocationIdRaw ? Number(dutyLocationIdRaw) : null

  const dutyLocationParam = dutyLocationId
    ? `&dutyLocationId=${dutyLocationId}`
    : ""
  const { isLoading: isAnalyticsDetailsLoading, data: analyticsDetails } =
    useGet<IAnalyticsRes>({
      url: `/v1/time-attendance/analytics${x ? `?workplaceId=${x}` : ""}${
        !x ? `?period=${timeline}` : `&period=${timeline}`
      }${dutyLocationParam}`,
      instanceKey: "ewaAxiosInstance",
      enabled: !!timeline,
    })
  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-between items-center pt-6">
        <h5 className="text-slate-900 text-lg font-bold text-padeBlack mb-3  ">
          {title}
        </h5>
        <div className="flex flex-col border-2 border-slate-200 rounded-md w-32 p-2">
          <p className="text-gray-500 text-[12px]">Timeline:</p>

          <Select
            size="small"
            defaultValue={
              timeline && timeline === "3d" ? "Last 3 days" : "Select timeline"
            }
            optionLabelProp="label"
            bordered={false}
            suffixIcon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="w-4 h-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="m19.5 8.25-7.5 7.5-7.5-7.5"
                />
              </svg>
            }
            onChange={setSelectedTimeline}
            options={[
              { value: "7d", label: "Last 7 days" },
              { value: "30d", label: "Last 30 days" },
              { value: "90d", label: "Last 90 days" },
            ]}
            className="[&_.ant-select-selector]:!p-0 [&_.ant-select-selection-item]:!font-bold [&_.ant-select-selection-item]:!text-slate-900"
          />
        </div>
      </div>

      {isAnalyticsDetailsLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
          {Array.from({ length: 4 }).map(() => (
            <div className="border-2 border-[#E2E8F0] rounded-md p-2 flex-1 mt-2">
              <Skeleton
                active
                title
                paragraph={{ rows: 2 }}
              />
            </div>
          ))}
        </div>
      ) : (
        <ShiftRateCardWrapper>
          <ShiftRateCard
            percentage={
              analyticsDetails?.data?.metrics?.shiftAdherence?.value || 0
            }
            rate={analyticsDetails?.data?.metrics?.shiftAdherence?.delta || 0}
            title="Shift adherence rate"
            message="Percentage of scheduled shifts that employees attended."
          />

          <ShiftRateCard
            percentage={
              analyticsDetails?.data?.metrics?.absenteeismRate?.value || 0
            }
            rate={analyticsDetails?.data?.metrics?.absenteeismRate?.delta || 0}
            title="Absenteeism Rate"
            message="Percentage of scheduled shifts where employees did not turn up at workplace."
          />

          <ShiftRateCard
            percentage={
              analyticsDetails?.data?.metrics?.timeAdherence?.value || 0
            }
            rate={analyticsDetails?.data?.metrics?.timeAdherence?.delta || 0}
            title="Time Adherence"
            message="Difference between total scheduled hours and actual hours worked. Negative values mean employees worked less than scheduled."
          />

          <ShiftRateCard
            percentage={
              analyticsDetails?.data?.metrics?.overtimeSubmissions?.value || 0
            }
            rate={
              analyticsDetails?.data?.metrics?.overtimeSubmissions?.delta || 0
            }
            title="Overtime Alert"
            message="Difference between total scheduled hours and actual hours required. Positive means employees worked more than scheduled."
          />
        </ShiftRateCardWrapper>
      )}
    </div>
  )
}
export default WorkPlaceAnalysisWrapper
